import { toFullUrl } from '../config/api';
import { Play, Pause, SkipForward, Music, ChevronRight } from 'lucide-react';
import { usePlayer } from '../context/PlayerContext';
import CommentSection from './CommentSection';
import LikeButton from './LikeButton';
import FireButton from './FireButton';

const THEMES = {
  classic: { label: 'Classic', description: 'Clean minimal player', preview: 'bg-[#141414] border-[#262626]' },
  neon: { label: 'Neon Glow', description: 'Cyberpunk neon vibes', preview: 'bg-[#0a0015] border-purple-500/50' },
  vinyl: { label: 'Vinyl', description: 'Record player aesthetic', preview: 'bg-[#1a1008] border-amber-800/50' },
  ember: { label: 'Ember', description: 'Fiery gradient style', preview: 'bg-gradient-to-r from-[#1a0000] to-[#0A0A0A] border-red-900/50' },
  glass: { label: 'Glass', description: 'Frosted glass modern', preview: 'bg-white/5 border-white/20' },
};

export { THEMES };

// Shared background + logo layer used by all themes
function ArtworkLayer({ bg, logo }) {
  return (
    <>
      {bg && <div className="absolute inset-0 z-0 overflow-hidden"><img src={bg} alt="" className="w-full h-full object-cover opacity-30" /><div className="absolute inset-0 bg-black/40" /></div>}
      {logo && <img src={logo} alt="" className="absolute top-3 right-3 w-10 h-10 sm:w-12 sm:h-12 object-contain z-20 opacity-70 drop-shadow-lg" data-testid="player-logo-badge" />}
    </>
  );
}

export function ThemedPlayer({ theme = 'classic', tracks = [], onNavigate, artwork = {} }) {
  const { currentTrack, isPlaying, playTrack, currentTime, duration, formatTime, seek } = usePlayer();
  const art = artwork[theme] || {};

  const playNext = () => {
    if (!currentTrack || tracks.length === 0) return;
    const idx = tracks.findIndex(t => t.id === currentTrack.id);
    const next = tracks[(idx + 1) % tracks.length];
    if (next) playTrack(next);
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  // ─── CLASSIC ───
  if (theme === 'classic') {
    return (
      <div className="relative bg-[#0A0A0A] overflow-hidden" data-testid="homepage-player" style={{ border: '1px solid #1a1a1a' }}>
        <ArtworkLayer bg={art.bg} logo={art.logo} />
        {currentTrack ? (
          <div className="relative z-10 flex flex-col sm:flex-row items-stretch gap-0">
            {/* Large cover art */}
            <div className="relative w-full sm:w-64 lg:w-72 aspect-square sm:aspect-auto sm:h-auto bg-[#111] flex-shrink-0 overflow-hidden">
              {currentTrack.cover_image ? (
                <img src={toFullUrl(currentTrack.cover_image)} alt={currentTrack.title}
                  className="w-full h-full object-cover"
                  style={{ imageRendering: 'auto', filter: 'contrast(1.05) saturate(1.1)' }}
                  loading="eager" />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-[#111]"><Music className="w-16 h-16 text-[#333]" /></div>
              )}
              {/* Gradient overlay on cover */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-[#0A0A0A]/80 hidden sm:block" />
              {/* Now playing badge */}
              {isPlaying && (
                <div className="absolute top-3 left-3 px-2 py-1 text-[9px] font-bold uppercase tracking-widest flex items-center gap-1.5"
                  style={{ background: 'var(--primary, #FF3B30)', color: '#fff' }}>
                  <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> NOW PLAYING
                </div>
              )}
            </div>
            {/* Track info + controls */}
            <div className="flex-1 p-5 sm:p-6 lg:p-8 w-full flex flex-col justify-center">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] mb-2" style={{ color: 'var(--primary, #FF3B30)' }}>
                {currentTrack.genre || 'TRACK'}
              </p>
              <h3 className="font-cabinet text-2xl lg:text-3xl font-black uppercase tracking-tight leading-tight">{currentTrack.title}</h3>
              <p className="text-[#666] text-sm mt-1 mb-5">{currentTrack.artist}</p>
              {/* Progress bar */}
              <div className="flex items-center gap-3 mb-5">
                <span className="text-[10px] font-mono text-[#555] w-10 text-right tabular-nums">{formatTime(currentTime)}</span>
                <div className="flex-1 relative h-1 bg-[#1a1a1a] group cursor-pointer" style={{ borderRadius: 'var(--radius)' }}>
                  <div className="absolute inset-y-0 left-0 transition-all" style={{ width: `${progress}%`, background: 'var(--primary, #FF3B30)', borderRadius: 'var(--radius)', boxShadow: `0 0 8px var(--primary, #FF3B30)40` }} />
                  <div className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    style={{ left: `${progress}%`, transform: `translateX(-50%) translateY(-50%)`, background: 'var(--primary, #FF3B30)', boxShadow: `0 0 6px var(--primary, #FF3B30)` }} />
                  <input type="range" min={0} max={duration || 100} value={currentTime} onChange={(e) => seek(Number(e.target.value))} className="absolute inset-0 w-full opacity-0 cursor-pointer" />
                </div>
                <span className="text-[10px] font-mono text-[#555] w-10 tabular-nums">{formatTime(duration)}</span>
              </div>
              {/* Controls */}
              <div className="flex items-center gap-3 flex-wrap">
                <button onClick={() => playTrack(currentTrack)}
                  className="w-12 h-12 flex items-center justify-center text-white transition-all hover:scale-110"
                  style={{ background: 'var(--primary, #FF3B30)', borderRadius: 'var(--radius)', boxShadow: `0 4px 15px var(--primary, #FF3B30)30` }}
                  data-testid="compact-play-pause">
                  {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
                </button>
                <button onClick={playNext} className="w-10 h-10 border border-[#262626] flex items-center justify-center text-[#555] hover:text-white hover:border-[#444] transition-colors" style={{ borderRadius: 'var(--radius)' }} data-testid="compact-skip">
                  <SkipForward className="w-4 h-4" />
                </button>
                <LikeButton itemType="track" itemId={currentTrack.id} size="md" />
                <FireButton itemType="track" itemId={currentTrack.id} size="md" />
                <div className="flex-1" />
                <button onClick={onNavigate}
                  className="flex items-center gap-2 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.15em] transition-all hover:text-white"
                  style={{ color: 'var(--primary, #FF3B30)', border: `1px solid var(--primary, #FF3B30)30`, borderRadius: 'var(--radius)' }}
                  data-testid="browse-all-tracks">
                  ALL TRACKS <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
              <CommentSection itemType="track" itemId={currentTrack.id} />
            </div>
          </div>
        ) : (
          <div className="relative z-10"><EmptyState tracks={tracks} playTrack={playTrack} onNavigate={onNavigate} /></div>
        )}
      </div>
    );
  }

  // ─── NEON ───
  if (theme === 'neon') {
    return (
      <div className="relative rounded-xl overflow-hidden" data-testid="homepage-player" style={{ background: 'linear-gradient(135deg, #0a0015 0%, #1a0030 50%, #0a0015 100%)', border: '1px solid rgba(168, 85, 247, 0.3)', boxShadow: '0 0 30px rgba(168, 85, 247, 0.15), inset 0 0 30px rgba(168, 85, 247, 0.05)' }}>
        <ArtworkLayer bg={art.bg} logo={art.logo} />
        {currentTrack ? (
          <div className="relative z-10 flex flex-col sm:flex-row items-center gap-0">
            <div className="relative w-full sm:w-52 aspect-square sm:aspect-auto sm:h-52 flex-shrink-0 overflow-hidden" style={{ borderRight: '1px solid rgba(168, 85, 247, 0.2)' }}>
              {currentTrack.cover_image ? (
                <img src={toFullUrl(currentTrack.cover_image)} alt={currentTrack.title} className="w-full h-full object-cover" style={{ filter: 'saturate(1.3) contrast(1.1)' }} />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-purple-900/20"><Music className="w-14 h-14 text-purple-400" /></div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a0015] via-transparent to-transparent" />
            </div>
            <div className="flex-1 p-6 w-full">
              <h3 className="text-xl font-bold uppercase tracking-wider mb-1" style={{ color: '#e0b0ff', textShadow: '0 0 20px rgba(168, 85, 247, 0.5)' }}>{currentTrack.title}</h3>
              <p className="text-purple-300/60 text-sm mb-5">{currentTrack.artist}</p>
              <div className="flex items-center gap-3 mb-5">
                <span className="text-xs font-mono text-purple-300/50 w-10 text-right">{formatTime(currentTime)}</span>
                <div className="flex-1 relative h-1 rounded-full bg-purple-900/40">
                  <div className="absolute inset-y-0 left-0 rounded-full" style={{ width: `${progress}%`, background: 'linear-gradient(90deg, #a855f7, #ec4899)', boxShadow: '0 0 10px rgba(168, 85, 247, 0.6)' }} />
                  <input type="range" min={0} max={duration || 100} value={currentTime} onChange={(e) => seek(Number(e.target.value))} className="absolute inset-0 w-full opacity-0 cursor-pointer" />
                </div>
                <span className="text-xs font-mono text-purple-300/50 w-10">{formatTime(duration)}</span>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={() => playTrack(currentTrack)} className="w-12 h-12 rounded-full flex items-center justify-center transition-all" data-testid="compact-play-pause" style={{ background: 'linear-gradient(135deg, #a855f7, #ec4899)', boxShadow: '0 0 20px rgba(168, 85, 247, 0.4)' }}>
                  {isPlaying ? <Pause className="w-5 h-5 text-white" /> : <Play className="w-5 h-5 text-white ml-0.5" />}
                </button>
                <button onClick={playNext} className="w-10 h-10 rounded-full border border-purple-500/30 flex items-center justify-center text-purple-300/60 hover:text-purple-200 hover:border-purple-400 transition-colors" data-testid="compact-skip">
                  <SkipForward className="w-4 h-4" />
                </button>
                <LikeButton itemType="track" itemId={currentTrack.id} size="md" />
                <FireButton itemType="track" itemId={currentTrack.id} size="md" />
                <div className="flex-1" />
                <button onClick={onNavigate} className="flex items-center gap-2 px-4 py-2 rounded-full border border-purple-500/30 text-purple-300/60 hover:text-purple-200 hover:border-purple-400 text-sm font-bold uppercase tracking-wider transition-colors" data-testid="browse-all-tracks">
                  ALL TRACKS <ChevronRight className="w-4 h-4" />
                </button>
              </div>
              <CommentSection itemType="track" itemId={currentTrack.id} />
            </div>
          </div>
        ) : (
          <div className="relative z-10"><EmptyState tracks={tracks} playTrack={playTrack} onNavigate={onNavigate} accent="purple" /></div>
        )}
      </div>
    );
  }

  // ─── VINYL ───
  if (theme === 'vinyl') {
    return (
      <div className="relative overflow-hidden" data-testid="homepage-player" style={{ background: 'linear-gradient(145deg, #1a1008 0%, #0d0a04 100%)', border: '1px solid rgba(180, 130, 50, 0.25)' }}>
        <ArtworkLayer bg={art.bg} logo={art.logo} />
        {currentTrack ? (
          <div className="relative z-10 flex flex-col sm:flex-row items-center gap-0">
            <div className="relative w-full sm:w-52 aspect-square sm:aspect-auto sm:h-52 flex-shrink-0 flex items-center justify-center overflow-hidden bg-[#0d0a04]/60">
              <div className={`w-40 h-40 sm:w-44 sm:h-44 rounded-full border-4 border-[#2a1f0a] flex items-center justify-center ${isPlaying ? 'vinyl-spin' : ''}`} style={{ background: 'radial-gradient(circle, #1a1008 0%, #0a0804 40%, #1a1008 41%, #0a0804 60%, #1a1008 61%)' }}>
                {currentTrack.cover_image ? (
                  <img src={toFullUrl(currentTrack.cover_image)} alt="" className="w-16 h-16 rounded-full object-cover border-2 border-amber-900/50" />
                ) : (
                  <div className="w-4 h-4 rounded-full bg-amber-600/60" />
                )}
              </div>
            </div>
            <div className="flex-1 p-6 w-full">
              <h3 className="text-xl font-bold uppercase tracking-tight mb-1 text-amber-100">{currentTrack.title}</h3>
              <p className="text-amber-600/60 text-sm mb-5">{currentTrack.artist}</p>
              <div className="flex items-center gap-3 mb-5">
                <span className="text-xs font-mono text-amber-600/50 w-10 text-right">{formatTime(currentTime)}</span>
                <div className="flex-1 relative h-1 bg-amber-900/30 rounded-full">
                  <div className="absolute inset-y-0 left-0 rounded-full bg-amber-500" style={{ width: `${progress}%` }} />
                  <input type="range" min={0} max={duration || 100} value={currentTime} onChange={(e) => seek(Number(e.target.value))} className="absolute inset-0 w-full opacity-0 cursor-pointer" />
                </div>
                <span className="text-xs font-mono text-amber-600/50 w-10">{formatTime(duration)}</span>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={() => playTrack(currentTrack)} className="w-12 h-12 rounded-full bg-amber-600 hover:bg-amber-500 flex items-center justify-center transition-colors" data-testid="compact-play-pause">
                  {isPlaying ? <Pause className="w-5 h-5 text-black" /> : <Play className="w-5 h-5 text-black ml-0.5" />}
                </button>
                <button onClick={playNext} className="w-10 h-10 rounded-full border border-amber-800/40 flex items-center justify-center text-amber-600/60 hover:text-amber-400 hover:border-amber-600 transition-colors" data-testid="compact-skip">
                  <SkipForward className="w-4 h-4" />
                </button>
                <LikeButton itemType="track" itemId={currentTrack.id} size="md" />
                <FireButton itemType="track" itemId={currentTrack.id} size="md" />
                <div className="flex-1" />
                <button onClick={onNavigate} className="flex items-center gap-2 px-4 py-2 rounded-full border border-amber-800/40 text-amber-600/60 hover:text-amber-400 hover:border-amber-600 text-sm font-bold uppercase tracking-wider transition-colors" data-testid="browse-all-tracks">
                  ALL TRACKS <ChevronRight className="w-4 h-4" />
                </button>
              </div>
              <CommentSection itemType="track" itemId={currentTrack.id} />
            </div>
          </div>
        ) : (
          <div className="relative z-10"><EmptyState tracks={tracks} playTrack={playTrack} onNavigate={onNavigate} accent="amber" /></div>
        )}
        <style>{`.vinyl-spin { animation: vinylSpin 3s linear infinite; } @keyframes vinylSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // ─── EMBER ───
  if (theme === 'ember') {
    return (
      <div className="relative overflow-hidden" data-testid="homepage-player" style={{ background: 'linear-gradient(135deg, #1a0000 0%, #0A0A0A 50%, #1a0500 100%)', border: '1px solid rgba(255, 59, 48, 0.2)', boxShadow: '0 0 40px rgba(255, 59, 48, 0.08)' }}>
        <ArtworkLayer bg={art.bg} logo={art.logo} />
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at bottom, rgba(255, 59, 48, 0.06) 0%, transparent 70%)' }} />
        {currentTrack ? (
          <div className="relative z-10 flex flex-col sm:flex-row items-center gap-0">
            <div className="relative w-full sm:w-52 aspect-square sm:aspect-auto sm:h-52 flex-shrink-0 overflow-hidden">
              {currentTrack.cover_image ? (
                <img src={toFullUrl(currentTrack.cover_image)} alt={currentTrack.title} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-red-900/10"><Music className="w-14 h-14 text-red-500/50" /></div>
              )}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#0A0A0A]/80" />
            </div>
            <div className="flex-1 p-6 w-full">
              <h3 className="text-xl font-bold uppercase tracking-wider mb-1" style={{ background: 'linear-gradient(90deg, #FF3B30, #FF6347, #DAA520)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{currentTrack.title}</h3>
              <p className="text-red-300/40 text-sm mb-5">{currentTrack.artist}</p>
              <div className="flex items-center gap-3 mb-5">
                <span className="text-xs font-mono text-red-400/40 w-10 text-right">{formatTime(currentTime)}</span>
                <div className="flex-1 relative h-1.5 bg-red-900/20 rounded-sm">
                  <div className="absolute inset-y-0 left-0 rounded-sm" style={{ width: `${progress}%`, background: 'linear-gradient(90deg, #FF3B30, #DAA520)', boxShadow: '0 0 8px rgba(255, 59, 48, 0.5)' }} />
                  <input type="range" min={0} max={duration || 100} value={currentTime} onChange={(e) => seek(Number(e.target.value))} className="absolute inset-0 w-full opacity-0 cursor-pointer" />
                </div>
                <span className="text-xs font-mono text-red-400/40 w-10">{formatTime(duration)}</span>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={() => playTrack(currentTrack)} className="w-12 h-12 flex items-center justify-center transition-all hover:scale-105" data-testid="compact-play-pause" style={{ background: 'linear-gradient(135deg, #FF3B30, #B91C1C)', boxShadow: '0 0 20px rgba(255, 59, 48, 0.3)' }}>
                  {isPlaying ? <Pause className="w-5 h-5 text-white" /> : <Play className="w-5 h-5 text-white ml-0.5" />}
                </button>
                <button onClick={playNext} className="w-10 h-10 border border-red-900/30 flex items-center justify-center text-red-400/40 hover:text-red-300 hover:border-red-700 transition-colors" data-testid="compact-skip">
                  <SkipForward className="w-4 h-4" />
                </button>
                <LikeButton itemType="track" itemId={currentTrack.id} size="md" />
                <FireButton itemType="track" itemId={currentTrack.id} size="md" />
                <div className="flex-1" />
                <button onClick={onNavigate} className="flex items-center gap-2 px-4 py-2 border border-red-900/30 text-red-400/40 hover:text-red-300 hover:border-red-700 text-sm font-bold uppercase tracking-wider transition-colors" data-testid="browse-all-tracks">
                  ALL TRACKS <ChevronRight className="w-4 h-4" />
                </button>
              </div>
              <CommentSection itemType="track" itemId={currentTrack.id} />
            </div>
          </div>
        ) : (
          <div className="relative z-10"><EmptyState tracks={tracks} playTrack={playTrack} onNavigate={onNavigate} accent="red" /></div>
        )}
      </div>
    );
  }

  // ─── GLASS ───
  return (
    <div className="relative rounded-2xl overflow-hidden" data-testid="homepage-player" style={{ background: 'rgba(255, 255, 255, 0.04)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255, 255, 255, 0.1)', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)' }}>
      <ArtworkLayer bg={art.bg} logo={art.logo} />
      {currentTrack ? (
        <div className="relative z-10 flex flex-col sm:flex-row items-center gap-0">
          <div className="relative w-full sm:w-48 aspect-square sm:aspect-auto sm:h-48 flex-shrink-0 overflow-hidden rounded-l-2xl">
            {currentTrack.cover_image ? (
              <img src={toFullUrl(currentTrack.cover_image)} alt={currentTrack.title} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-white/5"><Music className="w-12 h-12 text-white/30" /></div>
            )}
          </div>
          <div className="flex-1 p-5 w-full">
            <h3 className="text-xl font-bold uppercase tracking-tight text-white/90">{currentTrack.title}</h3>
            <p className="text-white/40 text-sm mb-4">{currentTrack.artist}</p>
            <div className="flex items-center gap-3 mb-4">
              <span className="text-xs font-mono text-white/30 w-10 text-right">{formatTime(currentTime)}</span>
              <div className="flex-1 relative h-1 rounded-full bg-white/10">
                <div className="absolute inset-y-0 left-0 rounded-full bg-white/70" style={{ width: `${progress}%` }} />
                <input type="range" min={0} max={duration || 100} value={currentTime} onChange={(e) => seek(Number(e.target.value))} className="absolute inset-0 w-full opacity-0 cursor-pointer" />
              </div>
              <span className="text-xs font-mono text-white/30 w-10">{formatTime(duration)}</span>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={() => playTrack(currentTrack)} className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center hover:bg-white transition-colors" data-testid="compact-play-pause">
                {isPlaying ? <Pause className="w-5 h-5 text-black" /> : <Play className="w-5 h-5 text-black ml-0.5" />}
              </button>
              <button onClick={playNext} className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center text-white/40 hover:text-white hover:border-white/40 transition-colors" data-testid="compact-skip">
                <SkipForward className="w-4 h-4" />
              </button>
              <LikeButton itemType="track" itemId={currentTrack.id} size="md" />
              <FireButton itemType="track" itemId={currentTrack.id} size="md" />
              <div className="flex-1" />
              <button onClick={onNavigate} className="flex items-center gap-2 px-4 py-2 rounded-full border border-white/15 text-white/40 hover:text-white hover:border-white/40 text-sm font-bold uppercase tracking-wider transition-colors" data-testid="browse-all-tracks">
                ALL TRACKS <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            <CommentSection itemType="track" itemId={currentTrack.id} />
          </div>
        </div>
      ) : (
        <div className="relative z-10"><EmptyState tracks={tracks} playTrack={playTrack} onNavigate={onNavigate} accent="white" /></div>
      )}
    </div>
  );
}

function EmptyState({ tracks, playTrack, onNavigate, accent = 'red' }) {
  const colors = {
    red: { btn: 'bg-[#FF3B30] hover:bg-white hover:text-[#0A0A0A]', icon: 'text-[#A3A3A3]', text: 'text-[#A3A3A3]', link: 'text-[#A3A3A3] hover:text-white' },
    purple: { btn: 'bg-purple-600 hover:bg-purple-400', icon: 'text-purple-400/50', text: 'text-purple-300/50', link: 'text-purple-300/50 hover:text-purple-200' },
    amber: { btn: 'bg-amber-600 hover:bg-amber-400 text-black', icon: 'text-amber-500/50', text: 'text-amber-600/50', link: 'text-amber-600/50 hover:text-amber-400' },
    white: { btn: 'bg-white/90 hover:bg-white text-black', icon: 'text-white/30', text: 'text-white/40', link: 'text-white/40 hover:text-white' },
  };
  const c = colors[accent] || colors.red;
  return (
    <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
      <Music className={`w-12 h-12 ${c.icon} mb-4`} />
      <p className={`${c.text} mb-6`}>Hit play on any track to start listening</p>
      <button onClick={() => { if (tracks.length > 0 && tracks[0]?.preview_url) playTrack(tracks[0]); else onNavigate(); }} className={`flex items-center gap-2 px-6 py-3 ${c.btn} text-sm font-bold uppercase tracking-wider transition-colors`} data-testid="start-listening-btn">
        {tracks.length > 0 && tracks[0]?.preview_url ? <><Play className="w-4 h-4" /> PLAY NOW</> : <><Music className="w-4 h-4" /> BROWSE TRACKS</>}
      </button>
      <button onClick={onNavigate} className={`mt-4 flex items-center gap-2 ${c.link} text-sm transition-colors`} data-testid="browse-all-tracks-empty">
        VIEW ALL TRACKS <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
}
